import { useQuery } from "@tanstack/react-query";
import { Button, Card, Divider, Layout, Progress, Skeleton, Typography } from "antd";
import React, { useContext, useEffect } from "react";

import { useUserAccountDetailsQuery } from "@Components/shared/queries";
import ActivityBreakdown from "@Components/user/ActivityBreakdown";
import ActivityCalendar from "@Components/user/ActivityCalendar";
import { QueryKeys } from "@Constants";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";
import SessionManager from "@Core/session";
import { useHistory } from "react-router";
import { IUserActivityMinimal } from "@Models/IActivityDetails";
import { IUserDetails } from "@Models/IUserDetails";
import ActivityService from "@Services/ActivityService";
import { getDuration, getProgressClass } from "@Utils";
import { Countries } from "../../countries";
import { EditOutlined } from "@mui/icons-material";
const { Title, Text } = Typography;
const { Content } = Layout;

interface IProps {
    profileProgress: number;
}

const ProfileCard: React.FC<IProps> = ({ profileProgress }: IProps) => {
    const { ui: uiState } = useContext(MainContext);

    const history = useHistory();

    const onNavigate = async (url: string) => {
        history.push(url);
    };

    const userAccountDetailsQuery = useUserAccountDetailsQuery();

    const userMinimalQuery = useQuery(
        [QueryKeys.ActivityCurrent],
        async () => {
            const service = new ActivityService();
            const result = await service.getUserMinimal(SessionManager.getServiceUser()?.userId);
            if (!result.hasErrors && result.value.length > 0) {
                return result.value as IUserActivityMinimal[];
            }
            return [];
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            initialData: [],
        }
    );

    /**
     * Render specialty info.
     * @param user
     */
    const renderSpecialty = (user: IUserDetails) => {
        return user.specialty ? (
            <div className="content-item">
                <Text>Specialty:</Text>
                <Title level={5} className="card-stat" style={{ marginBottom: "0px" }}>
                    {user.specialty ?? ""}
                </Title>
            </div>
        ) : (
            ""
        );
    };

    /**
     * Render the user's number of years training.
     * @param user
     */
    const renderYearsTraining = (user: IUserDetails) => {
        return user.yearsTraining ? (
            <div className="content-item">
                <Text>Years of Training:</Text>
                <Title level={5} className="card-stat" style={{ marginBottom: "0px" }}>
                    {user.yearsTraining ?? ""}
                </Title>
            </div>
        ) : (
            ""
        );
    };

    /**
     * Use the appropriate spelling of Activity.
     * @param value
     */
    const pluraliseActivity = (value?: number): string => {
        return value === 1 ? "Activity" : "Activities";
    };

    /**
     * Check if user is valid.
     */
    const userValid = () => {
        if (userAccountDetailsQuery.data) {
            return userAccountDetailsQuery.data.fullName && userAccountDetailsQuery.data.userId;
        }
        return false;
    };

    useEffect(() => {
        const refetch = async () => {
            await userMinimalQuery.refetch();
        };
        refetch().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return userValid() ? (
        <Card
            className="newUI-raised profile-card"
            bordered={false}
            style={uiState.mode === ViewMode.mobile ? { margin: "0 !important" } : null}
        >
            <Content style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div>
                    <Title level={3}>{userAccountDetailsQuery.data.fullName ?? "Anonymous"}</Title>
                    <Title level={5} style={{ marginTop: "0px" }}>
                        {Countries.get(userAccountDetailsQuery.data.countryCode ?? "GB")}
                    </Title>
                </div>

                <Divider style={{ margin: "12px 0" }} />

                {profileProgress ? (
                    <div className="profile-progress">
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Title level={5} style={{ marginTop: "0px" }}>
                                Your profile progress
                            </Title>
                            <Button onClick={() => onNavigate("/account#details")}>
                                <EditOutlined />
                            </Button>
                        </div>
                        <Progress
                            style={{ margin: "12px 0" }}
                            percent={profileProgress}
                            format={(percent) => <span style={{ fontWeight: "bold" }}>{percent}%</span>}
                            className={getProgressClass(profileProgress)}
                        />
                        <Divider style={{ margin: "15px 0" }} />
                    </div>
                ) : null}

                {renderSpecialty(userAccountDetailsQuery.data)}
                {renderYearsTraining(userAccountDetailsQuery.data)}
                <div className="content-item">
                    <Text>Completed:</Text>
                    <Title level={5} className="card-stat" style={{ marginBottom: "0px" }}>
                        {(userAccountDetailsQuery.data.totalActivities ?? 0) +
                            " " +
                            pluraliseActivity(userAccountDetailsQuery.data.totalActivities)}
                    </Title>
                </div>
                <div className="content-item" style={{ marginBottom: "20px" }}>
                    <Text>Total Time:</Text>
                    <Title level={5} className="card-stat" style={{ marginBottom: "0px" }}>
                        {getDuration(userAccountDetailsQuery.data.totalDuration ?? 0)}
                    </Title>
                </div>
                <ActivityCalendar userMinimal={userMinimalQuery.data} isFetching={userMinimalQuery.isFetching} />

                <ActivityBreakdown userMinimal={userMinimalQuery.data} isFetching={userMinimalQuery.isFetching} />
            </Content>
        </Card>
    ) : (
        <div style={{ padding: 30 }} className="newUI-raised profile-card">
            <Skeleton avatar active paragraph={{ rows: 10 }} />
        </div>
    );
};
export default ProfileCard;
